import React, { useRef } from 'react';
import { EDM_MERGE_FIELDS, EDM_UI, mergeLintError } from '../../../../utils/edm';
import type { EdmFixedLayout } from '../../../../hooks/useEdmCampaigns';
import MergeFieldChips from './MergeFieldChips';

type FieldKey = keyof EdmFixedLayout;

interface FieldDef {
  key: FieldKey;
  label: string;
  max: number;
  rows?: number;
  required: boolean;
  type?: 'url';
}

const FIELDS: FieldDef[] = [
  { key: 'heading', label: 'Heading', max: 120, required: true },
  { key: 'intro', label: 'Intro', max: 600, rows: 3, required: true },
  { key: 'body', label: 'Body', max: 4000, rows: 8, required: true },
  { key: 'ctaLabel', label: 'Button label', max: 40, required: false },
  { key: 'ctaUrl', label: 'Button link', max: 500, required: false, type: 'url' },
  { key: 'signOff', label: 'Sign-off', max: 200, rows: 2, required: true },
];

/** Whether the fixed layout is complete enough to freeze. */
export function fixedLayoutComplete(v: EdmFixedLayout): boolean {
  const required = [v.heading, v.intro, v.body, v.signOff].every((s) => (s ?? '').trim().length > 0);
  const label = (v.ctaLabel ?? '').trim();
  const url = (v.ctaUrl ?? '').trim();
  const ctaOk = label ? /^https:\/\/\S+$/i.test(url) : url === '' || /^https:\/\/\S+$/i.test(url);
  return required && ctaOk;
}

/** Lint errors per field (merge tokens). Blocks Save, Preview and Freeze. */
export function fixedLayoutLint(v: EdmFixedLayout): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const f of FIELDS) {
    if (f.type === 'url') continue;
    const err = mergeLintError(v[f.key] ?? '');
    if (err) out[f.key] = err;
  }
  return out;
}

const FixedLayoutFields: React.FC<{
  value: EdmFixedLayout;
  onChange: (v: EdmFixedLayout) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const lastFocused = useRef<{ key: FieldKey; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);
  const lint = fixedLayoutLint(value);

  const insert = (token: string) => {
    const target = lastFocused.current;
    const key: FieldKey = target?.key && target.key !== 'ctaUrl' ? target.key : 'body';
    const current = value[key] ?? '';
    const start = target?.key === key ? target.el.selectionStart ?? current.length : current.length;
    const end = target?.key === key ? target.el.selectionEnd ?? start : start;
    onChange({ ...value, [key]: current.slice(0, start) + token + current.slice(end) });
  };

  const ctaUrlError =
    (value.ctaLabel ?? '').trim() && !/^https:\/\/\S+$/i.test((value.ctaUrl ?? '').trim())
      ? 'Add a button link starting with https://'
      : (value.ctaUrl ?? '').trim() && !/^https:\/\/\S+$/i.test((value.ctaUrl ?? '').trim())
        ? 'Links must start with https://'
        : null;

  return (
    <div className="space-y-4">
      <MergeFieldChips fields={EDM_MERGE_FIELDS} onInsert={insert} disabled={disabled} />
      <p className="text-xs text-gray-400">
        Your logo, brand colour and the legal footer with the unsubscribe link are added automatically and cannot be removed.
      </p>
      {FIELDS.map((f) => {
        const id = `edm-fixed-${f.key}`;
        const v = value[f.key] ?? '';
        const common = {
          id,
          value: v,
          maxLength: f.max,
          disabled,
          className: EDM_UI.input,
          onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            lastFocused.current = { key: f.key, el: e.currentTarget };
          },
          onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onChange({ ...value, [f.key]: e.target.value }),
        };
        const err = f.type === 'url' ? ctaUrlError : lint[f.key];
        return (
          <div key={f.key}>
            <label htmlFor={id} className={EDM_UI.label}>
              {f.label}
              {!f.required && <span className="ml-1 text-xs text-gray-500">(optional)</span>}
            </label>
            {f.rows ? (
              <textarea rows={f.rows} {...common} />
            ) : (
              <input type={f.type === 'url' ? 'url' : 'text'} {...common} />
            )}
            {err && <p className="mt-1 text-xs text-red-400">{err}</p>}
          </div>
        );
      })}
    </div>
  );
};

export default FixedLayoutFields;
